import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import invariant from 'fbjs/lib/invariant';
import omit from 'lodash/omit';
import keys from 'lodash/keys';
import getFormData from 'get-form-data';
import { connect } from 'react-redux';

import { omitEmpty } from './form.util';
import { showErrorModal } from '../../redux/errors/actions.errors';
import { bugsnagLeaveBreadcrumb, bugsnagNotify } from '../../util/bugsnag';

const BUGSNAG_VALIDATION_SAMPLE_RATE = 1;

const sampleValidationBreadcrumb = () => Math.random() < BUGSNAG_VALIDATION_SAMPLE_RATE;

const summarizeValidationError = (humanizedError) => {
  const { error, property, message } = humanizedError || {};
  return {
    elementId: property,
    property,
    keyword: error?.keyword,
    schemaPath: error?.schemaPath,
    message,
  };
};

const propTypes = {
  children: PropTypes.node.isRequired,

  omitEmpty: PropTypes.bool,
  total: PropTypes.bool,
  enforceValidation: PropTypes.bool,

  onAjvError: PropTypes.func.isRequired,
  onFormInput: PropTypes.func,
  onFormChange: PropTypes.func,
  onFormSubmit: PropTypes.func,
  onValidationError: PropTypes.func,

  prevalidate: PropTypes.func,
  validate: PropTypes.func,
  postvalidate: PropTypes.func,
};
const propKeys = keys(propTypes);
class Form extends Component {
  errors = [];
  ref = createRef();
  static propTypes = propTypes;
  static childContextTypes = {
    errors: PropTypes.arrayOf(PropTypes.shape()),
    resetErrors: PropTypes.func,
  };
  static defaultProps = {
    enforceValidation: false,
    omitEmpty: false,
    total: false,
    onFormInput: undefined,
    onFormChange: undefined,
    onFormSubmit: undefined,
    onValidationError: undefined,
    validate: undefined,
    prevalidate: (form) => form,
    postvalidate: (form) => form,
  };
  node = () => this.ref.current;
  getChildContext = () => ({ errors: this.errors, resetErrors: this.resetErrors });
  setErrors = (errors = []) => {
    const { onValidationError } = this.props;
    invariant(Array.isArray(errors), 'Validate should return an Array.');

    const wasEmpty = this.errors.length === 0;
    const becameNonEmpty = errors.length > 0;
    this.errors = errors;
    this.forceUpdate();

    if (wasEmpty && becameNonEmpty && sampleValidationBreadcrumb()) {
      bugsnagLeaveBreadcrumb('Form validation failed', {
        count: errors.length,
        first: summarizeValidationError(errors[0]),
      });
    }

    if (!onValidationError) return;
    invariant(typeof onValidationError === 'function', 'onValidationError should be a function.');
    onValidationError(this.errors);
  };
  resetErrors = () => this.setErrors([]);

  prevalidate = (form) => {
    const { prevalidate } = this.props;
    if (!prevalidate) return Promise.resolve(form);
    invariant(typeof prevalidate === 'function', 'Prevalidate prop should be a function.');
    const nextForm = prevalidate(form);
    return Promise.resolve(nextForm);
  };
  validate = (prevalidatedForm, inputName) => {
    const { validate } = this.props;
    if (!validate) return Promise.resolve(prevalidatedForm);
    invariant(typeof validate === 'function', 'Validate prop should be a function.');
    invariant(typeof prevalidatedForm === 'object', 'Prevalidate should return a form object.');
    const nextForm = this.props.omitEmpty ? omitEmpty(prevalidatedForm) : prevalidatedForm;
    const args = this.props.total ? [nextForm] : [nextForm, inputName, this.errors];

    return validate(...args).catch((errors) => {
      this.setErrors(errors);
      return Promise.resolve(null);
    });
  };
  postvalidate = (validatedForm) => {
    const { postvalidate } = this.props;
    if (!postvalidate || !validatedForm) {
      return Promise.resolve(validatedForm);
    }
    this.setErrors([]);
    invariant(typeof postvalidate === 'function', 'Postvalidate prop should be a function.');
    const nextForm = postvalidate(validatedForm);
    return Promise.resolve(nextForm);
  };
  emitEvent = (handleEvent, inputName, event) => {
    const form = getFormData(this.node());

    if (!this.shouldValidate(handleEvent)) {
      if (typeof handleEvent === 'function') {
        handleEvent(form);
      }
      return;
    }
    if (handleEvent) {
      invariant(typeof handleEvent === 'function', 'Event handler should be a function.');
    }
    this.prevalidate(form)
      .then((prevalidatedForm) => this.validate(prevalidatedForm, inputName))
      .then(this.postvalidate)
      .then((nextForm) => {
        if (nextForm && typeof handleEvent === 'function') {
          handleEvent(nextForm, event);
        }
      })
      .catch(this.props.onAjvError);
  };

  handleFormChange = (event) => {
    event.persist();
    const { onFormInput, onFormChange } = this.props;
    let handler;
    if (event.nativeEvent.type === 'input') {
      this.lastNode = event.target;
      handler = onFormInput;
    } else {
      handler = onFormChange;
    }
    this.emitEvent(handler, event.target.name, event);
    event.stopPropagation();
  };
  handleFormFocus = (event) => {
    this.lastNode = event.target;
  };
  handleFormBlur = (event) => {
    event.persist();
    let eventHandler;
    if (event.target === this.lastNode) {
      eventHandler = this.props.onFormChange;
      this.lastNode = null;
    }
    this.emitEvent(eventHandler, event.target.name, event);
  };
  handleFormSubmit = (event) => {
    event.persist();
    event.preventDefault();
    this.emitEvent(this.props.onFormSubmit, undefined, event);
  };

  reset() {
    this.node().reset();
  }
  hasErrors() {
    return this.errors.length !== 0;
  }

  shouldValidate(handleEvent) {
    const { enforceValidation } = this.props;
    if (this.hasErrors() || enforceValidation) return true;
    return typeof handleEvent === 'function';
  }
  render() {
    const { props } = this;
    const restProps = omit(props, propKeys);
    return (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <form
        {...restProps}
        onChange={this.handleFormChange}
        onBlur={this.handleFormBlur}
        onFocus={this.handleFormFocus}
        onSubmit={this.handleFormSubmit}
        ref={this.ref}
      >
        {props.children}
      </form>
    );
  }
}

const mapDisparchToProps = (dispatch) => ({
  onAjvError(error) {
    const action = showErrorModal(error.message, 500, error);
    dispatch(action);
    bugsnagNotify(error);
  },
});

export default connect(null, mapDisparchToProps, undefined, { forwardRef: true })(Form);

export { Form as RawForm };

/**
 * @see docs/worklog/maintenance/20093-investigate-facebook-auth-errors/rfc-frontend.md
 */
